import type { AppLlmApi, DataClassification, LlmConfigV2, LlmProvider, LlmRegisteredProvider, LlmRequest, LlmResponse } from '@yada/types';
import { detectViewportType, type DownloadProgressCallback } from './viewport';
import { getSystemPromptForApp } from './appPrompts';
import { parseToolInvocation } from './previewParser';
import { PASSIVE_CONTEXT_PREFIX } from './passiveContext';
import { LLM_CONFIG_KEY, loadLlmConfig, saveLlmConfig } from './llmSecrets';

export { loadLlmConfig };

export const DEFAULT_WEBLLM_MODEL = 'Llama-3.2-3B-Instruct-q4f16_1-MLC';

/** Remote-call timeout (30 s) — prevents fetch from hanging indefinitely */
const TIMEOUT_MS = 30_000;

const CONFIG_KEY = LLM_CONFIG_KEY;
const CONSENT_KEY = 'yada:llm:consent';
export const DEVICE_LLM_KEY = 'yada:device:llm';

export interface DeviceLlmConfig {
  enabled: boolean;
  model: string;
}

/**
 * Get device-bound local LLM configuration (stored strictly on this physical machine).
 */
export function getDeviceLlmConfig(): DeviceLlmConfig {
  try {
    const raw = localStorage.getItem(DEVICE_LLM_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DeviceLlmConfig>;
      return {
        enabled: Boolean(parsed.enabled),
        model: parsed.model || DEFAULT_WEBLLM_MODEL,
      };
    }
  } catch {}

  // Legacy fallback / migration: if yada:llm:config had local-webllm enabled
  try {
    const rawConfig = localStorage.getItem(CONFIG_KEY);
    if (rawConfig) {
      const config = JSON.parse(rawConfig) as LlmConfigV2;
      if (config.provider?.type === 'local-webllm') {
        const viewport = detectViewportType();
        const model =
          config.webLlmModelsByViewport?.[viewport] ||
          config.provider.model ||
          DEFAULT_WEBLLM_MODEL;
        return { enabled: true, model };
      }
    }
  } catch {}

  return { enabled: false, model: DEFAULT_WEBLLM_MODEL };
}

/**
 * Update device-bound local LLM configuration without syncing across the account.
 */
export function setDeviceLlmConfig(config: DeviceLlmConfig): void {
  localStorage.setItem(DEVICE_LLM_KEY, JSON.stringify(config));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('yada:llm:changed'));
  }
}

/** Internal alias for backward compat — LlmConfigV2 covers both old and new shapes */
type LlmConfig = LlmConfigV2;

type ConsentStore = Record<string, Record<DataClassification, boolean>>;

export interface LlmLastError {
  /** Short reason code */
  code: 'provider-error' | 'webllm-unavailable' | 'auth-error' | 'network-error' | 'local-only';
  /** Human-readable message */
  message: string;
  /** ISO timestamp */
  at: string;
}

export interface WebLlmLoadingProgress {
  /** 0–1 fraction of model weights downloaded from CDN */
  downloadProgress: number;
  /** 0–1 fraction of model loaded into GPU memory */
  loadProgress: number;
  /** Human-readable status text */
  text: string;
}

export type ConsentCallback = (params: {
  appId: string;
  appName: string;
  classification: DataClassification;
  dataLabel?: string;
  /** Human-readable name of the remote provider receiving data */
  providerLabel?: string;
}) => Promise<boolean>;

/**
 * LlmManager — unified LLM provider abstraction with consent gate.
 *
 * One instance owned by CustomRuntime. Each app gets a scoped API via createApi().
 *
 * Consent rules (.ai/docs/llm-privacy.md):
 *   local provider        → every classification proceeds; nothing leaves the device
 *   remote provider       → every request needs the user's consent for (appId, classification);
 *                           personal-remote consent also covers structured-anonymous
 *   personal-local        → refused on a remote provider
 *   includesOthersContent → refused on a remote provider, whatever the classification
 *
 * Apps label their own requests, so the label never decides whether data may
 * leave the device without asking. The consent callback is set by the shell (via
 * LlmConsentContext) once the React tree is mounted. Until then, remote requests
 * return null.
 */
export type AgentStartCallback = (appId: string, prompt?: string) => void;
export type AgentEndCallback = (suggestion: { content: string; onAccept: (c: string) => Promise<unknown> | unknown; prompt?: string; appId?: string; kind?: 'answer' | 'action' } | null) => void;
export type AgentDeltaCallback = (chunk: string) => void;
export type AgentToolExecutor = (tool: string, args: Record<string, unknown>) => Promise<{
  success: boolean;
  message?: string;
  result?: unknown;
}>;
export type AgentToolConfirmationCheck = (tool: string) => boolean;

export type WebLlmDownloadProgressCallback = DownloadProgressCallback;

export class LlmManager {
  private consentCallback: ConsentCallback | null = null;
  private agentRevision = 0;
  private appContextResolver: ((appId: string) => Promise<string>) | null = null;

  private agentExecutionCallback: ((tool: string, result: Awaited<ReturnType<AgentToolExecutor>>) => void) | null = null;
  private agentStartCallback: AgentStartCallback | null = null;
  private agentEndCallback: AgentEndCallback | null = null;
  private agentDeltaCallback: AgentDeltaCallback | null = null;
  private webLlmProgressCallback: DownloadProgressCallback | null = null;
  private lastError: LlmLastError | null = null;
  private errorListeners: Array<(err: LlmLastError | null) => void> = [];
  private webLlmLoadingState: WebLlmLoadingProgress | null = null;
  private loadingListeners: Array<(state: WebLlmLoadingProgress | null) => void> = [];


  setAppContextResolver(resolve: (appId: string) => Promise<string>): void {
    this.appContextResolver = resolve;
  }

  /**
   * Called by the shell UI to receive WebLLM model download progress events.
   * The callback fires during the first use of local-webllm when weights are
   * being fetched from the CDN and cached in the browser's Cache Storage.
   */
  setWebLlmProgressCallback(cb: DownloadProgressCallback | null): void {
    this.webLlmProgressCallback = cb;
  }

  setConsentCallback(cb: ConsentCallback): void {
    this.consentCallback = cb;
  }

  setWebLlmLoadingState(state: WebLlmLoadingProgress | null): void {
    this.webLlmLoadingState = state;
    this.loadingListeners.forEach(l => l(state));
  }

  getWebLlmLoadingState(): WebLlmLoadingProgress | null {
    return this.webLlmLoadingState;
  }

  onLoadingChange(cb: (state: WebLlmLoadingProgress | null) => void): () => void {
    this.loadingListeners.push(cb);
    return () => {
      this.loadingListeners = this.loadingListeners.filter(l => l !== cb);
    };
  }

  setAgentCallbacks(onStart: AgentStartCallback, onEnd: AgentEndCallback, onDelta?: AgentDeltaCallback, onExecution?: (tool: string, result: Awaited<ReturnType<AgentToolExecutor>>) => void): void {
    this.agentStartCallback = onStart;
    this.agentEndCallback = onEnd;
    this.agentDeltaCallback = onDelta ?? null;
    this.agentExecutionCallback = onExecution ?? null;
  }

  cancelAgent(): void {
    this.agentRevision++;
  }

  getLastError(): LlmLastError | null {
    return this.lastError;
  }

  onErrorChange(cb: (err: LlmLastError | null) => void): () => void {
    this.errorListeners.push(cb);
    return () => {
      this.errorListeners = this.errorListeners.filter(l => l !== cb);
    };
  }

  private setLastError(err: LlmLastError | null): void {
    this.lastError = err;
    this.errorListeners.forEach(l => l(err));
  }

  async invokeAgent(
    appId: string,
    appName: string,
    request: LlmRequest,
    onAccept: (content: string) => Promise<void> | void,
    executeTool?: AgentToolExecutor,
    requiresConfirmation?: AgentToolConfirmationCheck,
  ): Promise<void> {
    const userPrompt = request.messages.filter(m => m.role === 'user').slice(-1)[0]?.content;
    const revision = ++this.agentRevision;
    this.agentStartCallback?.(appId, userPrompt);
    let messages = request.messages;
    const executedCalls = new Set<string>();
    let turns = 0;
    const recordResult = (invocation: { tool: string; arguments: Record<string, unknown> }, content: string, result: Awaited<ReturnType<AgentToolExecutor>>) => {
      this.agentExecutionCallback?.(invocation.tool, result);
      messages = [...messages, { role: 'assistant', content }, {
        role: 'user',
        content: `[TOOL RESULT for ${invocation.tool}]\n${JSON.stringify(result)}\n\nUse this result to answer accurately and continue remaining requested steps. Never repeat an applied action. Only success: true establishes a change. Return one tool JSON object for the next step, or concise prose when finished.`,
      }];
    };
    const run = async (): Promise<void> => {
      try {
        while (turns++ < 24 && revision === this.agentRevision) {
          const response = await this.complete(appId, appName, { ...request, messages }, chunk => {
            if (revision === this.agentRevision) this.agentDeltaCallback?.(chunk);
          });
          if (revision !== this.agentRevision) return;
          if (!response) { this.agentEndCallback?.(null); return; }
          const invocation = executeTool ? parseToolInvocation(response.content) : null;
          if (!invocation) {
            this.agentEndCallback?.({ content: response.content, onAccept, prompt: userPrompt, appId, kind: 'answer' });
            return;
          }
          const signature = `${invocation.tool}:${JSON.stringify(invocation.arguments)}`;
          const execute = async () => {
            if (requiresConfirmation?.(invocation.tool)) {
              if (executedCalls.has(signature)) return { success: false, message: 'This exact action was already executed. Read current data instead of repeating it.' };
              executedCalls.add(signature);
            }
            try { return await executeTool!(invocation.tool, invocation.arguments); }
            catch (error) { return { success: false, message: error instanceof Error ? error.message : String(error) }; }
          };
          if (requiresConfirmation?.(invocation.tool)) {
            let accepted = false;
            this.agentEndCallback?.({ content: response.content, prompt: userPrompt, appId, kind: 'action',
              onAccept: async () => {
                if (accepted) throw new Error('This prepared action has already been accepted.');
                if (revision !== this.agentRevision) throw new Error('This action is no longer current. Submit the request again.');
                accepted = true;
                this.agentStartCallback?.(appId);
                const result = await execute();
                if (revision !== this.agentRevision) return result;
                recordResult(invocation, response.content, result);
                if (!result.success) {
                  this.agentEndCallback?.({ content: result.message || 'Action failed. No completion was confirmed.', onAccept, appId, kind: 'answer' });
                  throw new Error(result.message || 'Action failed.');
                }
                await run();
                return result;
              },
            });
            return;
          }
          recordResult(invocation, response.content, await execute());
        }
        if (revision === this.agentRevision) this.agentEndCallback?.({
          content: 'The action limit was reached. Some requested steps may remain; review the execution results before continuing.',
          onAccept, prompt: userPrompt, appId, kind: 'answer',
        });
      } catch {
        if (revision === this.agentRevision) this.agentEndCallback?.(null);
      }
    };
    await run();
  }

  getProvider(): LlmProvider {
    try {
      // 1. Device-local WebLLM check (strictly bound to this physical machine)
      const deviceLlm = getDeviceLlmConfig();
      if (deviceLlm.enabled) {
        return { type: 'local-webllm', model: deviceLlm.model || DEFAULT_WEBLLM_MODEL };
      }

      // 2. Account-synced remote configuration
      const raw = localStorage.getItem(CONFIG_KEY);
      if (!raw) return null;
      const config = JSON.parse(raw) as LlmConfig;

      // If a registered provider is active, synthesise an LlmProvider from it
      const active = this.resolveActiveRegisteredProvider(config);
      if (active) {
        return { type: 'remote-openai', model: active.model, baseUrl: active.provider.baseUrl };
      }

      if (config.provider?.type === 'remote-anthropic' || config.provider?.type === 'remote-openai') {
        return config.provider;
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Returns the active registered provider + the currently-selected model for it,
   * or null if no registry selection is active.
   */
  private resolveActiveRegisteredProvider(
    config: LlmConfig,
  ): { provider: LlmRegisteredProvider; model: string } | null {
    if (!config.activeRegisteredProviderId || !config.registeredProviders?.length) return null;
    const entry = config.registeredProviders.find((p) => p.id === config.activeRegisteredProviderId);
    if (!entry) return null;
    // The model to use is stored on the legacy provider field when a registered provider is active
    const model = config.provider?.type === 'remote-openai' ? config.provider.model : 'gpt-4o-mini';
    return { provider: entry, model };
  }

  private getConsentStore(): ConsentStore {
    try {
      return JSON.parse(localStorage.getItem(CONSENT_KEY) ?? '{}') as ConsentStore;
    } catch {
      return {};
    }
  }

  private hasRemoteConsent(appId: string, classification: DataClassification): boolean {
    const granted = this.getConsentStore()[appId];
    return granted?.[classification] === true
      || (classification === 'structured-anonymous' && granted?.['personal-remote'] === true);
  }

  private hasDeclinedRemote(appId: string, classification: DataClassification): boolean {
    return this.getConsentStore()[appId]?.[classification] === false;
  }

  private recordConsent(appId: string, classification: DataClassification, granted: boolean): void {
    const store = this.getConsentStore();
    if (!store[appId]) store[appId] = {} as Record<DataClassification, boolean>;
    store[appId][classification] = granted;
    localStorage.setItem(CONSENT_KEY, JSON.stringify(store));
  }

  async complete(appId: string, appName: string, request: LlmRequest, onChunk?: (chunk: string) => void): Promise<LlmResponse | null> {
    const provider = this.getProvider();
    if (!provider) return null;

    const isLocal = provider.type === 'local-webllm';

    if (request.classification === 'personal-local' && !isLocal) {
      console.warn(`[LlmManager] ${appId} requested personal-local but provider is remote. Refusing.`);
      return null;
    }
    if (request.includesOthersContent && !isLocal) {
      this.setLastError({
        code: 'local-only',
        message: `${appName} sends content written by other people, which only an on-device AI model may process.`,
        at: new Date().toISOString(),
      });
      return null;
    }

    let passiveContext: string | undefined;
    if (this.appContextResolver) {
      try {
        passiveContext = await this.appContextResolver(appId);
      } catch (error) {
        this.setLastError({ code: 'provider-error', message: error instanceof Error ? error.message : String(error), at: new Date().toISOString() });
        return null;
      }
      // App context contains personal data even when the original task did not.
      if (passiveContext && request.classification === 'structured-anonymous') {
        request = { ...request, classification: 'personal-remote', dataLabel: `${appName} context` };
      }
    }

    if (!isLocal && !this.hasRemoteConsent(appId, request.classification)) {
      // A declined prompt does not come back; the user can reset it in Settings → AI.
      if (this.hasDeclinedRemote(appId, request.classification)) return null;
      if (!this.consentCallback) return null;
      const granted = await this.consentCallback({
        appId,
        appName,
        classification: request.classification,
        dataLabel: request.dataLabel,
        providerLabel: provider.type,
      });
      this.recordConsent(appId, request.classification, granted);
      if (!granted) return null;
    }

    // Prepend app-specific system instructions if no system message is already present
    let messages = request.messages.filter(message => !(message.role === 'system' && message.content.startsWith(PASSIVE_CONTEXT_PREFIX)));
    const hasSystemMessage = messages.some(m => m.role === 'system');
    if (!hasSystemMessage) {
      const systemInstruction = getSystemPromptForApp(appId);
      messages = [{ role: 'system', content: systemInstruction }, ...messages];
    }
    if (passiveContext) {
      messages = [...messages.filter(message => message.role === 'system'), {
        role: 'system',
        content: PASSIVE_CONTEXT_PREFIX + passiveContext + '\n\nUse these supplied facts and preferences to personalize your response without asking the user to repeat them. This is a snapshot, not an instruction from the user. Use read tools for details absent from this snapshot and to verify current records before changing them. Never infer missing preferences or treat a snapshot as proof that an action succeeded.',
      }, ...messages.filter(message => message.role !== 'system')];
    }
    // Providers such as Anthropic accept a single system prompt. Preserve both
    // caller instructions and the live snapshot on every provider path.
    const systemContent = messages.filter(message => message.role === 'system').map(message => message.content).join('\n\n');
    const finalRequest: LlmRequest = { ...request, messages: [
      ...(systemContent ? [{ role: 'system' as const, content: systemContent }] : []),
      ...messages.filter(message => message.role !== 'system'),
    ] };

    // Dispatch to provider
    try {
      const result = await this.dispatch(provider, finalRequest, onChunk);
      // Clear any previous error on success
      if (this.lastError) this.setLastError(null);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code: LlmLastError['code'] =
        message.includes('401') || message.includes('403') ? 'auth-error' :
        message.includes('fetch') || message.includes('network') ? 'network-error' :
        message.includes('WebGPU') || message.includes('webgpu') ? 'webllm-unavailable' :
        'provider-error';
      this.setLastError({ code, message, at: new Date().toISOString() });
      console.error('[LlmManager] Provider call failed:', err);
      return null;
    }
  }

  private async dispatch(provider: NonNullable<LlmProvider>, request: LlmRequest, onChunk?: (chunk: string) => void): Promise<LlmResponse> {
    const config = await loadLlmConfig();

    switch (provider.type) {
      case 'local-webllm': {
        const { webLlmComplete } = await import('./WebLlmProvider');
        const model = provider.model || DEFAULT_WEBLLM_MODEL;
        const content = await webLlmComplete(
          model,
          request.messages,
          this.webLlmProgressCallback ?? undefined,
          onChunk,
        );
        return { content, usedProvider: provider };
      }

      case 'remote-openai': {
        // Prefer registered-provider key + baseUrl over legacy fields
        const activeReg = this.resolveActiveRegisteredProvider(config);
        const apiKey = activeReg ? activeReg.provider.apiKey : config.openaiKey;
        if (!apiKey) throw new Error('OpenAI API key not configured');
        const baseUrl = (provider.baseUrl ?? 'https://api.openai.com/v1').replace(/\/$/, '');
        const openaiController = new AbortController();
        const openaiTimeout = setTimeout(() => openaiController.abort(), TIMEOUT_MS);
        let res: Response;
        try {
          res = await fetch(`${baseUrl}/chat/completions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${apiKey}`,
            },
            body: JSON.stringify({
              model: provider.model,
              messages: request.messages,
            }),
            signal: openaiController.signal,
          });
        } finally {
          clearTimeout(openaiTimeout);
        }
        if (!res!.ok) throw new Error(`OpenAI error: ${res!.status} ${await res!.text()}`);
        const data = await res.json() as { choices: Array<{ message: { content: string } }> };
        return { content: data.choices[0].message.content, usedProvider: provider };
      }

      case 'remote-anthropic': {
        if (!config.anthropicKey) throw new Error('Anthropic API key not configured');
        // Anthropic messages API expects system prompt separate from user/assistant turns
        const systemMsg = request.messages.find(m => m.role === 'system');
        const turns = request.messages.filter(m => m.role !== 'system');
        const anthropicController = new AbortController();
        const anthropicTimeout = setTimeout(() => anthropicController.abort(), TIMEOUT_MS);
        let res: Response;
        try {
          res = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': config.anthropicKey,
              'anthropic-version': '2023-06-01',
            },
            body: JSON.stringify({
              model: provider.model,
              max_tokens: 4096,
              ...(systemMsg ? { system: systemMsg.content } : {}),
              messages: turns,
            }),
            signal: anthropicController.signal,
          });
        } finally {
          clearTimeout(anthropicTimeout);
        }
        if (!res!.ok) throw new Error(`Anthropic error: ${res!.status} ${await res!.text()}`);
        const data = await res.json() as { content: Array<{ text: string }> };
        return { content: data.content[0].text, usedProvider: provider };
      }
    }
  }

  createApi(appId: string, appName: string): AppLlmApi {
    const manager = this;
    return {
      get provider() { return manager.getProvider(); },
      get isLocal() { return manager.getProvider()?.type === 'local-webllm'; },
      complete: (request) => manager.complete(appId, appName, request),
    };
  }
}

// ── Configuration for the Settings UI ─────────────────────────────────────────
// Read with loadLlmConfig(): API keys are stored encrypted (./llmSecrets.ts).
export async function setLlmConfig(config: LlmConfig): Promise<void> {
  try {
    await saveLlmConfig(config);
  } catch (error) {
    // Encryption unavailable on this device: keep the keys where they were rather
    // than lose them; loadLlmConfig() migrates them once encryption works.
    console.warn('[LlmManager] Could not encrypt LLM API keys; keeping them unencrypted.', error);
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('yada:llm:changed'));
    window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
  }
}

// ── Consent management exported for Settings UI ───────────────────────────────

/** Read the full consent store for display in the settings UI. */
export function getLlmConsentStore(): Record<string, Record<string, boolean>> {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) ?? '{}') as Record<string, Record<string, boolean>>;
  } catch {
    return {};
  }
}

/**
 * Forget the consent decision for a specific app + classification combination,
 * whether it was granted or declined. The next remote request from the app asks
 * again — giving the user ongoing control over their data.
 */
export function revokeLlmConsent(appId: string, classification: string): void {
  try {
    const store = JSON.parse(localStorage.getItem(CONSENT_KEY) ?? '{}') as Record<string, Record<string, boolean>>;
    if (store[appId]) {
      delete store[appId][classification];
      if (Object.keys(store[appId]).length === 0) {
        delete store[appId];
      }
    }
    localStorage.setItem(CONSENT_KEY, JSON.stringify(store));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
    }
  } catch {
    // ignore
  }
}

/**
 * Revoke ALL consent records for all apps.
 * Users regain full control — every app will need to re-ask before sending data.
 */
export function revokeAllLlmConsent(): void {
  localStorage.removeItem(CONSENT_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('yada:accountSetting:changed'));
  }
}
